import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Ban, CheckCircle2, Send, Trash2, UserCog, FileText, Building2, CreditCard, Boxes, PlusCircle, Users, History, ShieldCheck, Copy } from "lucide-react";
import OfferBuilderPanel from "@/components/OfferBuilderPanel";
import OptionsPanel from "@/components/OptionsPanel";
import { EntrepriseMetiersDialog } from "@/components/admin/EntrepriseMetiersDialog";
import { loadMetiers } from "@/components/metiers/metierUtils";

const TABS = [
  { key: "general", label: "Général", icon: Building2 },
  { key: "abonnement", label: "Abonnement", icon: CreditCard },
  { key: "modules", label: "Modules", icon: Boxes },
  { key: "options", label: "Options", icon: PlusCircle },
  { key: "utilisateurs", label: "Utilisateurs", icon: Users },
  { key: "historique", label: "Historique", icon: History },
  { key: "securite", label: "Sécurité", icon: ShieldCheck },
];

export default function AdminEntrepriseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [tab, setTab] = useState("general");
  const [metiersOpen, setMetiersOpen] = useState(false);
  const [metierRef, setMetierRef] = useState({});
  useEffect(() => { loadMetiers().then((ms) => setMetierRef(Object.fromEntries(ms.map((m) => [m.code, m])))).catch(() => {}); }, []);
  const metierLabel = (code) => (metierRef[code] ? `${metierRef[code].emoji || ""} ${metierRef[code].label}`.trim() : code);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/entreprises/${id}`);
      setEnt(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [id]);

  const act = async (path, label, body = {}) => {
    try {
      await api.post(`/admin/entreprises/${id}/${path}`, body);
      toast.success(label);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette entreprise et toutes ses données ?")) return;
    try {
      await api.delete(`/admin/entreprises/${id}`);
      toast.success("Entreprise supprimée");
      navigate("/admin/entreprises");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleCSE = async () => {
    const newVal = !ent.cse_enabled;
    try {
      await api.post(`/admin/entreprises/${id}/cse`, { enabled: newVal });
      toast.success(newVal ? "Module CSE activé" : "Module CSE désactivé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleKbisReq = async () => {
    const newVal = !ent.kbis_required;
    try {
      await api.post(`/admin/entreprises/${id}/kbis-required`, { enabled: newVal });
      toast.success(newVal ? "KBIS demandé" : "Obligation KBIS levée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setMultiSite = async (enabled, network_mode) => {
    try {
      await api.post(`/admin/entreprises/${id}/multi-site`, { enabled, network_mode: network_mode || ent.network_mode || "multi" });
      toast.success(enabled ? `Multi-établissements activé (${(network_mode || ent.network_mode) === "franchise" ? "franchise" : "réseau intégré"})` : "Multi-établissements désactivé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revokeSessions = async () => {
    if (!window.confirm(`Révoquer toutes les sessions actives de ${ent.name} ? Tous les utilisateurs (chef + salariés) devront se reconnecter.`)) return;
    try {
      const { data } = await api.post(`/admin/entreprises/${id}/revoke-sessions`);
      toast.success(`${data.affected_users} utilisateur(s) déconnecté(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copié"); };

  if (!ent) return <div className="p-10 text-sm">Chargement…</div>;

  return (
    <div className="min-h-full" data-testid="admin-ent-detail">
      <PageHeader
        eyebrow={`${ent.pag_cli || "— PAG-CLI en cours —"} · ${ent.code}`}
        title={ent.name}
        sub={`${ent.salaries?.length || 0} salariés · SIRET ${ent.siret || (ent.en_projet ? "— (entreprise en projet)" : "—")} · Créée le ${new Date(ent.created_at).toLocaleDateString("fr-FR")}`}
        right={
          <Button variant="ghost" onClick={() => navigate("/admin/entreprises")} data-testid="admin-back-btn" className="font-semibold">
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour
          </Button>
        }
      />

      {/* Tabs bar */}
      <div className="border-b border-[#DEE2E6] bg-white sticky top-[52px] z-10" data-testid="ent-tabs">
        <div className="px-6 lg:px-10 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ent-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#868E96] hover:text-[#0A0A0A]"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 lg:p-10">
        {tab === "general" && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="tab-general">
            <div className="lg:col-span-2 bg-white border border-[#DEE2E6] p-6 space-y-4">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-2">Identification</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Numéro client PAG" value={
                  <span className="flex items-center gap-2 font-mono text-[#059669] font-bold">
                    {ent.pag_cli || "—"}
                    {ent.pag_cli && <button onClick={() => copy(ent.pag_cli)} data-testid="copy-pag-cli" className="text-[#868E96] hover:text-[#0A0A0A]"><Copy className="h-3 w-3" /></button>}
                  </span>
                } />
                <Info label="Code entreprise" value={<span className="font-mono text-[#002FA7] font-bold">{ent.code}</span>} />
                <Info label="Raison sociale" value={ent.name} />
                <Info label="SIRET" value={<span className="font-mono">{ent.siret || "—"}</span>} />
                <Info label="Statut" value={<StatusPill status={ent.status} />} />
                <Info label="Créée le" value={new Date(ent.created_at).toLocaleDateString("fr-FR")} />
                <div className="col-span-2" data-testid="ent-metiers-block">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#868E96]">Module métier</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {ent.metier_code ? <span className="inline-flex items-center bg-[#0A0A0A] text-[#FFD700] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" data-testid="ent-metier-principal">{metierLabel(ent.metier_code)}</span> : <span className="text-sm text-[#868E96]" data-testid="ent-metier-principal">Non défini{ent.secteur ? ` (secteur libre : ${ent.secteur})` : ""}</span>}
                    {(ent.metiers_secondaires || []).map((c) => <span key={c} className="inline-flex items-center border border-[#0A0A0A] px-2 py-1 text-[10px] font-bold uppercase tracking-wider" data-testid={`ent-metier-secondaire-${c}`}>{metierLabel(c)}</span>)}
                    <button type="button" onClick={() => setMetiersOpen(true)} className="text-xs font-semibold text-[#002FA7] hover:underline" data-testid="ent-metiers-edit">Modifier</button>
                  </div>
                  {ent.metier_code && <div className="font-mono text-[10px] text-[#868E96] mt-1">{ent.metier_code}{(ent.metiers_secondaires || []).length ? ` · ${ent.metiers_secondaires.join(" · ")}` : ""}</div>}
                </div>
              </div>
              {ent.chef && (
                <>
                  <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mt-6 mb-2">Chef d&apos;entreprise</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Info label="Nom" value={`${ent.chef.prenom} ${ent.chef.nom}`} />
                    <Info label="Code de connexion" value={<span className="font-mono">{ent.chef.code}</span>} />
                    <Info label="Email" value={ent.chef.email || "—"} />
                    <Info label="Téléphone" value={<span className="font-mono">{ent.chef.telephone || "—"}</span>} />
                  </div>
                </>
              )}
            </div>
            <div className="space-y-4">
              <div className="bg-white border border-[#DEE2E6] p-5 space-y-2">
                <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3">Actions rapides</div>
                <Button onClick={() => act("mark-paid", "Paiement enregistré", { months: 1 })} data-testid="admin-mark-paid" className="w-full justify-start bg-[#059669] hover:bg-[#047857] text-white">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Marquer payé (1 mois)
                </Button>
                <Button onClick={() => act("activate", "Entreprise activée")} data-testid="admin-activate" variant="outline" className="w-full justify-start">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Activer
                </Button>
                <Button onClick={() => act("block", "Entreprise bloquée")} data-testid="admin-block" variant="outline" className="w-full justify-start">
                  <Ban className="h-4 w-4 mr-2" /> Bloquer
                </Button>
                <Button onClick={() => act("warning", "Avertissement envoyé")} data-testid="admin-warning" variant="outline" className="w-full justify-start">
                  <Send className="h-4 w-4 mr-2" /> Envoyer avertissement
                </Button>
                <Button onClick={remove} data-testid="admin-delete" variant="outline" className="w-full justify-start border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white">
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </div>
              {ent.status === "blocked" && (
                <div className="p-4 border border-[#FCA5A5] bg-[#FEF2F2] text-sm">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-[#E60000] mt-0.5" />
                    <div>
                      <div className="font-bold text-[#991B1B]">Entreprise bloquée</div>
                      <p className="text-xs">Accès suspendu pour chef et salariés.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "abonnement" && (
          <div className="bg-white border border-[#DEE2E6] p-6 space-y-4" data-testid="tab-abonnement">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96]">Abonnement en cours</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Info label="Formule actuelle" value={<b className="uppercase">{ent.tier_v2 || ent.plan || "—"}</b>} />
              <Info label="Cycle" value={ent.last_payment_period || "—"} />
              <Info label="Dernier paiement" value={ent.last_paid_at ? new Date(ent.last_paid_at).toLocaleDateString("fr-FR") : "—"} />
              <Info label="Prochaine échéance" value={ent.payment_due_date ? new Date(ent.payment_due_date).toLocaleDateString("fr-FR") : "—"} />
            </div>
            {ent.stripe_subscription_id && (
              <div className="text-[10px] font-mono text-[#868E96]">Stripe subscription : {ent.stripe_subscription_id}</div>
            )}
            <div className="pt-4 border-t border-[#DEE2E6] text-xs text-[#495057]">
              Pour modifier la formule ou activer des modules à la carte, utilisez les onglets <b>Modules</b> et <b>Options</b>.
            </div>
          </div>
        )}

        {tab === "modules" && <OfferBuilderPanel entrepriseId={id} entreprise={ent} onSaved={load} />}

        {tab === "options" && <OptionsPanel entrepriseId={id} />}

        {tab === "utilisateurs" && (
          <div className="bg-white border border-[#DEE2E6] p-6 space-y-6" data-testid="tab-utilisateurs">
            {ent.chef && (
              <div>
                <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3">Chef d&apos;entreprise (1)</div>
                <div className="py-3 flex justify-between items-center text-sm border border-[#DEE2E6] px-4 bg-[#F8F9FA]" data-testid={`admin-chef-${ent.chef.code}`}>
                  <div>
                    <div className="font-semibold">{ent.chef.prenom} {ent.chef.nom} <span className="text-[10px] uppercase font-bold text-[#002FA7] ml-2">· CHEF</span></div>
                    <div className="text-xs text-[#868E96]">{ent.chef.email || "— aucun email —"} · {ent.chef.telephone || "—"}</div>
                  </div>
                  <div className="font-mono text-xs text-[#002FA7] font-bold">{ent.chef.code}</div>
                </div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3">Salariés ({ent.salaries?.length || 0})</div>
              {ent.salaries?.length ? (
                <div className="divide-y divide-[#DEE2E6] border border-[#DEE2E6]">
                  {ent.salaries.map((s) => (
                    <div key={s.id} className="py-3 px-4 flex justify-between items-center text-sm" data-testid={`admin-salarie-${s.code}`}>
                      <div>
                        <div className="font-semibold">{s.prenom} {s.nom}</div>
                        <div className="text-xs text-[#868E96]">{s.poste || "—"} · {s.contrat || "—"}</div>
                      </div>
                      <div className="font-mono text-xs">{s.code}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-[#868E96]">Aucun salarié pour l&apos;instant.</div>}
            </div>
          </div>
        )}

        {tab === "historique" && <HistoryTimeline entrepriseId={id} />}

        {tab === "securite" && (
          <div className="bg-white border border-[#DEE2E6] p-6 space-y-3" data-testid="tab-securite">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-2">Modules sécurité</div>
            <Button onClick={toggleCSE} data-testid="admin-toggle-cse" variant="outline" className={`w-full justify-start ${ent.cse_enabled ? "bg-[#ECFDF5] border-[#059669] text-[#059669]" : ""}`}>
              <UserCog className="h-4 w-4 mr-2" /> CSE / CE : {ent.cse_enabled ? "Activé" : "Désactivé"}
            </Button>
            <Button onClick={toggleKbisReq} data-testid="admin-toggle-kbis" variant="outline" className={`w-full justify-start ${ent.kbis_required ? "bg-[#FFFBEB] border-[#D97706] text-[#D97706]" : ""}`}>
              <FileText className="h-4 w-4 mr-2" /> KBIS obligatoire : {ent.kbis_required ? "Oui" : "Non"}
            </Button>
            <div className={`border p-3 space-y-2 ${ent.multi_site_enabled ? "border-[#002FA7] bg-[#F5F7FF]" : "border-[#DEE2E6]"}`} data-testid="admin-multisite-block">
              <Button onClick={() => setMultiSite(!ent.multi_site_enabled)} data-testid="admin-toggle-multisite" variant="outline" className={`w-full justify-start ${ent.multi_site_enabled ? "bg-white border-[#002FA7] text-[#002FA7]" : ""}`}>
                <Building2 className="h-4 w-4 mr-2" /> Multi-établissements : {ent.multi_site_enabled ? "Activé" : "Désactivé"}
              </Button>
              {ent.multi_site_enabled && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="uppercase tracking-[0.2em] font-semibold text-[#868E96]">Mode</span>
                  {[["multi", "Réseau intégré"], ["franchise", "Franchise"]].map(([k, l]) => (
                    <button key={k} onClick={() => setMultiSite(true, k)} data-testid={`admin-network-mode-${k}`}
                      className={`h-8 px-3 font-bold uppercase tracking-wider border transition-colors ${(ent.network_mode || "multi") === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{l}</button>
                  ))}
                </div>
              )}
            </div>
            {ent.kbis_storage_path && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/entreprise/${id}/kbis`} target="_blank" rel="noreferrer" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="admin-download-kbis">
                  <FileText className="h-4 w-4 mr-2" /> Télécharger KBIS
                </Button>
              </a>
            )}

            <div className="border-t border-[#DEE2E6] pt-4 mt-4">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E60000] mb-2">Zone critique — RGPD</div>
              <div className="text-xs text-[#495057] mb-3">
                Révoque instantanément toutes les sessions actives (chef + salariés). Les utilisateurs seront déconnectés au prochain appel API.
                {ent.sessions_revoked_at && (
                  <span className="block mt-1 font-mono">Dernière révocation : {new Date(ent.sessions_revoked_at).toLocaleString("fr-FR")}</span>
                )}
              </div>
              <Button onClick={revokeSessions} data-testid="admin-revoke-sessions" variant="outline" className="w-full justify-start border-[#E60000] text-[#E60000] hover:bg-[#FEF2F2]">
                <ShieldCheck className="h-4 w-4 mr-2" /> Révoquer toutes les sessions
              </Button>
            </div>
          </div>
        )}
      </div>
      {metiersOpen && <EntrepriseMetiersDialog open onOpenChange={setMetiersOpen} entreprise={ent} onSaved={load} />}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { label: "Active", cls: "bg-[#ECFDF5] text-[#065F46]" },
    pending_payment: { label: "En attente de paiement", cls: "bg-[#FFFBEB] text-[#92400E]" },
    blocked: { label: "Bloquée", cls: "bg-[#FEF2F2] text-[#991B1B]" },
    inactive: { label: "Inactive", cls: "bg-[#F3F4F6] text-[#374151]" },
    warning: { label: "En avertissement", cls: "bg-[#FEF3C7] text-[#92400E]" },
  };
  const cur = map[status] || map.inactive;
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cur.cls}`}>{cur.label}</span>;
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

function HistoryTimeline({ entrepriseId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: r } = await api.get(`/admin/entreprises/${entrepriseId}/history`);
        if (!cancelled) setData(r);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [entrepriseId]);

  const filters = [
    { key: "all", label: "Tout" },
    { key: "entreprise", label: "Statut", match: (k) => k.startsWith("entreprise.") },
    { key: "offer", label: "Offre", match: (k) => k.startsWith("offer.") },
    { key: "option", label: "Options", match: (k) => k.startsWith("option.") },
    { key: "payment", label: "Paiements", match: (k) => k.startsWith("payment.") },
    { key: "cancel", label: "Résiliations", match: (k) => k.startsWith("cancellation.") || k.startsWith("subscription.") },
    { key: "profile", label: "Profil / KBIS", match: (k) => k.startsWith("profile.") || k.startsWith("kbis.") },
  ];
  const activeFilter = filters.find((f) => f.key === filter);
  const events = (data?.events || []).filter((e) => filter === "all" || activeFilter?.match?.(e.kind));

  if (loading) return <div className="bg-white border border-[#DEE2E6] p-6 text-sm text-[#868E96]" data-testid="tab-historique">Chargement…</div>;

  return (
    <div className="bg-white border border-[#DEE2E6]" data-testid="tab-historique">
      <div className="p-6 border-b border-[#DEE2E6] flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96]">Timeline 360°</div>
          <div className="text-sm mt-1"><b>{data?.total ?? 0}</b> événements — du plus récent au plus ancien</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const qs = filter && filter !== "all" ? `?filter=${filter}` : "";
                const res = await api.get(`/admin/entreprises/${entrepriseId}/history/pdf${qs}`, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `timeline_${entrepriseId.slice(0, 8)}_${filter}_${new Date().toISOString().slice(0, 10)}.pdf`;
                link.click();
                toast.success(`Export PDF téléchargé${filter !== "all" ? ` (filtre: ${filter})` : ""}`);
              } catch (e) { toast.error("Impossible d'exporter le PDF"); }
            }}
            data-testid="ent-history-export-pdf"
            className="inline-flex items-center gap-1.5 h-8 px-3 border border-[#0A0A0A] text-[10px] uppercase tracking-wider font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            📄 Export PDF{filter !== "all" ? ` (${filter})` : " (RGPD)"}
          </button>
          <a href="/admin/audit" className="text-xs font-semibold text-[#002FA7] underline" data-testid="ent-history-audit-link">Audit RGPD complet →</a>
        </div>
      </div>

      <div className="p-4 border-b border-[#DEE2E6] flex flex-wrap gap-2 bg-[#F8F9FA]" data-testid="ent-history-filters">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`ent-history-filter-${f.key}`}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${filter === f.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DEE2E6] text-[#495057] hover:border-[#0A0A0A]"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {events.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#868E96]" data-testid="ent-history-empty">
          Aucun événement pour ce filtre.
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-[27px] top-6 bottom-6 w-px bg-[#DEE2E6]" aria-hidden />
          <ul className="p-6 space-y-4">
            {events.map((e) => (
              <li key={e.id} className="relative pl-12" data-testid={`ent-history-event-${e.kind}`}>
                <span
                  className="absolute left-4 top-1.5 w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: e.color }}
                  aria-hidden
                />
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-display font-bold text-sm" style={{ color: e.color }}>{e.label}</span>
                  <span className="text-[11px] text-[#868E96] font-mono">
                    {e.timestamp ? new Date(e.timestamp).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </span>
                  {e.actor?.code && (
                    <span className="text-[10px] uppercase tracking-wider text-[#495057] font-semibold bg-[#F3F4F6] px-2 py-0.5">
                      par {e.actor.code}
                    </span>
                  )}
                </div>
                {e.message && <div className="mt-1 text-sm text-[#495057] italic">« {e.message} »</div>}
                {e.meta && Object.keys(e.meta).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#868E96] font-mono">
                    {Object.entries(e.meta).map(([k, v]) => v == null || v === "" ? null : (
                      <span key={k}><b>{k}:</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
